import { useMemo } from 'react';
import { type Resolve } from '@doxyme/types';
import { type SessionParticipant } from '../../webrtc/session';
import { getSessionParticipantsMap } from '../../features/session/session.selectors';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import { getChatUserIdFromIdentity } from '../../features/realtime/realtime.helpers';
import {
  type RealtimeMember,
  RealtimeRole,
} from '../../features/realtime/realtime.types';
import { useAppSelector } from '../useReduxToolkit';

export type PatientViewCallParticipant = Resolve<
  Partial<RealtimeMember> & SessionParticipant & { chatUserId?: string }
>;

export type UsePatientViewCallParticipantsReturnType = {
  /**
   * Map of current call participants, keyed by participant ID.
   */
  callParticipantsMap: Record<string, PatientViewCallParticipant>;
  /**
   * A list of active participants in the current call.
   * Excludes participants who have already left the call.
   */
  callParticipants: PatientViewCallParticipant[];
  /**
   * The participant currently sharing their screen, if any.
   */
  screenSharingParticipant?: PatientViewCallParticipant;
};

export const usePatientViewCallParticipants =
  (): UsePatientViewCallParticipantsReturnType => {
    const sessionParticipantsMap: Record<string, SessionParticipant> =
      useAppSelector(getSessionParticipantsMap);
    const realtimeMembersMap = useAppSelector(getAllMembersMap);

    const callParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          Object.entries(sessionParticipantsMap).map(([id, participant]) => {
            const member = realtimeMembersMap[id];

            if (member && member.identity.role === RealtimeRole.Provider) {
              return [
                id,
                {
                  ...member,
                  ...participant,
                  username: member.state?.username ?? participant.username,
                  chatUserId: getChatUserIdFromIdentity(member?.identity),
                },
              ];
            }

            return [id, { ...participant, username: participant.username }];
          }),
        ),
      [sessionParticipantsMap, realtimeMembersMap],
    );

    const callParticipants = Object.values(callParticipantsMap);

    const screenSharingParticipant = callParticipants.find(
      (participant) => participant.screenElement,
    );

    return {
      callParticipantsMap,
      callParticipants,
      screenSharingParticipant,
    };
  };
